/* eslint-disable react/no-danger */
import { useState, useEffect, useRef } from 'react';

import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Tween, Timeline, ScrollTrigger } from 'react-gsap';
import Slider from 'react-slick';
import { Row, Col, Container, Nav, NavLink, TabContent, TabPane } from 'reactstrap';
import { useRecoilState } from 'recoil';

import { getConfig } from '../../apis/config';
import Collapse from '../../components/includes/collapse-birthday';
import useI18n from '../../hooks/use-i18n';
import { languages, contentLanguageMap } from '../../language.config';
import { birthdayVideo, obt3EnabledMarkets } from '../../links.config';
import { config } from '../../recoil/atoms/config';
import getTranslationKeys, { PAGEID } from '../../utils/i18nRosseta';
import { HowZenyumWorksComparisonSlider } from '../../utils/slider.config';
import { setCookie } from '../../utils/storage';
import { fetchBannerConfig, gumletAWSClean, loadGumlet, useUtmParam as useUtmData } from '../../utils/utils';

import '../../styles/globals/globals.scss';
import '../../styles/header.scss';
import '../../styles/birthday.scss';

const Birthday = () => {
  const i18n = useI18n();
  const birthdayPage = i18n.t('zenyum_birthday');
  const orientationMsg = i18n.t('orientation');
  const codes = i18n.activeLocale.split('-');
  const country = codes.length === 2 ? codes[0] : 'sg';
  const lang = codes[1] || 'en';
  const router = useRouter();
  const query = router && router.query;
  const [isPortrait, setisPortrait] = useState(false);
  const [toggleAssessment, setToggleAssessment] = useState(0);
  const [toggleTreatment, setToggleTreatment] = useState(0);
  const [kickstartTab, setKickstartTab] = useState('1');
  const [smileTab, setSmileTab] = useState('1');
  const [configData, setConfig] = useRecoilState(config);
  const [utmParams, setUtmParams] = useState('');
  const comparisonSlider = useRef();
  const productLinks = birthdayVideo[country][lang] || birthdayVideo[country];

  const beforeAfterImages = [
    { before: 'before-shevon.png', after: 'after-shevon.png' },
    { before: 'before-gaoyang.png', after: 'after-gaoyang.png' },
    { before: 'before-michelle.png', after: 'after-michelle.png' },
    { before: 'before-alyssa.png', after: 'after-alyssa.png' },
    { before: 'before-joven.png', after: 'after-joven.png' },
    { before: 'before-leovalentine.png', after: 'after-leovalentine.png' },
    { before: 'before-shas.png', after: 'after-shas.png' }
  ];

  const fetchConfig = async () => {
    const configRes = await getConfig(i18n.activeLocale);
    if (configRes) {
      const bannerData = fetchBannerConfig(configRes);
      if (bannerData) {
        setConfig({ ...bannerData });
      }
    }
  };

  useEffect(() => {
    setisPortrait(window.innerWidth < 768);
    const colorText = document.querySelector('.bday-content-intro h2 strong');
    if (typeof (colorText) !== 'undefined' && colorText != null) {
      const textLetter = colorText.textContent.split('');
      let textResult = '';
      textLetter.forEach((char) => {
        textResult += (char.trim() === '') ? ' ' : `<span>${char}</span>`;
      });
      colorText.innerHTML = textResult;
    }

    if (!config.loaded) fetchConfig();

    window.addEventListener('resize', () => {
      setisPortrait(window.innerWidth < 768);
    });
    loadGumlet();
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [country]);

  useEffect(() => {
    Object.keys(query).forEach((utmParam) => {
      if (utmParam.startsWith('utm_') || utmParam === 'gclid') {
        setCookie(utmParam, query[utmParam]);
      }
    });
  }, [query]);

  useEffect(() => {
    setTimeout(() => {
      setUtmParams(useUtmData('how-zenyum-works'));
    }, 50);
  }, [query]);

  const bannerDesktop = configData?.banners?.alignerDesktop
  && configData.banners.birthdayDesktop.length > 0
    ? configData.banners.birthdayDesktop[0] : {};
  const bannerMobile = configData?.banners?.alignerMobile
  && configData.banners.birthdayMobile.length > 0
    ? configData.banners.birthdayDesktop[0] : {};

  return (
    <div className="wrapper birthday-page">
      <Head>
        <title>{birthdayPage.page_title}</title>
        <meta charSet="utf-8" />
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
        <meta name="description" content={birthdayPage.page_description} />
        <meta name="keywords" content={birthdayPage.page_keywords} />
        <meta name="author" content="Zenyum" />
        <meta httpEquiv="content-language" content={lang} />
        <meta property="og:title" content={birthdayPage.page_title} />
        <meta property="og:description" content={birthdayPage.page_description} />
        <meta property="og:image" content={`${process.env.NEXT_PUBLIC_BASE_URL}img/FB_zenyum_02.jpg`} />
        <meta httpEquiv="content-language" content={contentLanguageMap[i18n.activeLocale]} />

        <link rel="alternate" hrefLang="en" href="https://www.zenyum.com/sg-en" />
        <link rel="alternate" hrefLang="en-SG" href="https://www.zenyum.com/sg-en" />
        <link rel="alternate" hrefLang="en-MY" href="https://www.zenyum.com/my-en" />
        <link rel="alternate" hrefLang="en-HK" href="https://www.zenyum.com/hk-en" />
        <link rel="alternate" hrefLang="zh-HK" href="https://www.zenyum.com/hk-zh" />
        <link rel="alternate" hrefLang="zh-TW" href="https://www.zenyum.com/tw-zh" />
        <link rel="alternate" hrefLang="en-MY" href="https://www.zenyum.com/my-en" />
        <link rel="alternate" hrefLang="th" href="https://www.zenyum.com/th-th" />
        <link rel="alternate" hrefLang="id" href="https://www.zenyum.com/id-id" />
        <link rel="alternate" hrefLang="vi" href="https://www.zenyum.com/vn-vi" />

        <link rel="icon" href="/img/favicon.png" sizes="32x32" />
        <link rel="icon" href="/img/favicon.png" sizes="192x192" />
        <link rel="apple-touch-icon" href="/img/favicon.png" />
        {lang !== 'vi' && lang !== 'th' && lang !== 'zh' && (
        <link rel="stylesheet" href="/fonts/fonts.css" />
        )}
        {lang === 'vi' && <link rel="stylesheet" href="/fonts/fonts-vi.css" />}
        {lang === 'th' && <link rel="stylesheet" href="/fonts/fonts-th.css" />}
        {lang === 'zh' && <link rel="stylesheet" href="/fonts/fonts-zh.css" />}
        <link rel="stylesheet" href="/vendor/bootstrap/css/bootstrap.min.css" async={true} />

        <link rel="stylesheet" href="/vendor/slick/slick.min.css" />
        <link rel="stylesheet" href="/vendor/slick/slick-theme.min.css" />
      </Head>
      <header className="birthday-header">
        <Container>
          <nav>
            <Link href="/[lng]/" as={`/${i18n.activeLocale}/`}>
              <a className="nav-logo">
                {' '}
                <img src="/img/logo.svg" alt="zenyum logo" />
              </a>
            </Link>
          </nav>
        </Container>

        <div className="orientation-msg">
          <div>
            <h2>{orientationMsg.title}</h2>
            <p>{orientationMsg.description}</p>
          </div>
        </div>
      </header>
      <main>
        <div
          className="birthday-banner"
          data-bg={gumletAWSClean(`${isPortrait ? bannerMobile?.bannerUrl
            : bannerDesktop?.bannerUrl}`)}
          data-gmlazy="false"
        >
          <Container>
            <div className="birthday-banner__content">
              <h1>{configData?.birthdayBannerText}</h1>
              <p>
                {birthdayPage.banner_scroll}
                <span className="scroll-icon" />
              </p>
            </div>
          </Container>
          <div className="birthday-banner__offer">
            <Container className="bday-container">
              <div className="d-flex justify-content-between">
                <div className="d-md-flex align-items-center d-none">
                  <img src="/img/birthday/icon-gift.svg" alt="Icon Gift" />
                  <p>
                    <span>{birthdayPage.banner_offer_subtext}</span>
                    {birthdayPage.banner_offer_text}
                  </p>
                </div>
                <a
                  href={`${obt3EnabledMarkets.includes(country) ? process.env.NEXT_PUBLIC_OBT3_LINK
                    : process.env.NEXT_PUBLIC_OBT_LINK}${country}-${lang}/intro/${utmParams}`}
                  className="web-btn ml-5 my-auto"
                >
                  {birthdayPage.banner_offer_button}

                </a>
              </div>
            </Container>
          </div>
        </div>
        <div className="birthday-content">
          <section className="bday-content-intro">
            <div className="scroll-line-anim">
              <ScrollTrigger trigger=".bday-content-intro" start="top center" end="bottom center" scrub={0}>
                <Timeline>
                  <Tween from={{ height: '0' }} to={{ height: '100%' }} ease="none"><span /></Tween>
                </Timeline>
              </ScrollTrigger>
            </div>
            <Container className="bday-container-sm">
              <ScrollTrigger
                trigger=".bday-content-intro .container"
                start="-20px center"
                end="-20px center"
                animation={true}
                toggleActions="play none reverse none"
              >
                <Tween
                  from={{ opacity: 0, y: 70 }}
                  to={{ opacity: 1, y: 0 }}
                  reverse={true}
                  stagger="0.3"
                  duration="0.8"
                >
                  <h2 dangerouslySetInnerHTML={{ __html: birthdayPage.section_intro.title_text }} />
                  <p>{birthdayPage.section_intro.description}</p>
                </Tween>
              </ScrollTrigger>
            </Container>
          </section>
          <div className="bday-content-wrap">
            <section className="bday-content-block" id="anim-block1">
              <Container className="bday-container-sm">
                <ScrollTrigger
                  trigger="#anim-block1"
                  start="-100 center"
                  end="-100 center"
                  animation={true}
                  toggleActions="play none reverse none"
                >
                  <Tween
                    from={{ opacity: 0, y: 70 }}
                    to={{ opacity: 1, y: 0 }}
                    reverse={true}
                    stagger="0.3"
                    duration="0.8"
                  >
                    <div className="title-block">
                      <h3>{birthdayPage.section_content_1.title}</h3>
                    </div>
                  </Tween>
                </ScrollTrigger>
                <div className="scroll-line-anim scroll-gap-anim top-anim">
                  <ScrollTrigger trigger="#anim-block1 .top-anim" start="top center" end="bottom center" scrub={0}>
                    <Timeline>
                      <Tween from={{ height: '0' }} to={{ height: '100%' }} ease="none"><span /></Tween>
                    </Timeline>
                  </ScrollTrigger>
                </div>
                <div className="content-box">
                  <div className="scroll-box-anim">
                    <ScrollTrigger
                      trigger="#anim-block1 .scroll-box-anim"
                      start="5px center"
                      end="bottom center"
                      scrub={0}
                    >
                      <Timeline>
                        <Tween from={{ width: '0' }} to={{ width: '50%' }} ease="none"><span /></Tween>
                        <Tween from={{ height: '0' }} to={{ height: '100%' }} ease="none"><span /></Tween>
                        <Tween from={{ width: '0' }} to={{ width: '50%' }} ease="none"><span /></Tween>
                      </Timeline>
                    </ScrollTrigger>
                  </div>
                  <Row>
                    <ScrollTrigger
                      trigger="#anim-block1 .content-box"
                      start="-100 center"
                      end="-100 center"
                      animation={true}
                      toggleActions="play none reverse none"
                    >
                      <Tween
                        from={{ opacity: 0, y: 70 }}
                        to={{ opacity: 1, y: 0 }}
                        reverse={true}
                        stagger="0.3"
                        duration="0.8"
                      >
                        <div className="col-md-6">
                          <div className="content-box__col">
                            <img data-src="/img/birthday/pre-assessment.png" alt="Zenyum" />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="content-box__col">
                            <p className="block-label">{birthdayPage.select_text}</p>
                            <div className="accordion-box">
                              {birthdayPage.section_content_1.data
                              && birthdayPage.section_content_1.data.length > 0
                              && birthdayPage.section_content_1.data.map((row, index) => (
                                row.question && (
                                <Collapse
                                  id={index}
                                  isToggled={toggleAssessment === index}
                                  setTogglequestion={setToggleAssessment}
                                  title={row.question}
                                  content={row.answer}
                                />
                                )
                              ))}
                            </div>
                          </div>
                        </div>
                      </Tween>
                    </ScrollTrigger>
                  </Row>
                </div>
                <div className="scroll-line-anim scroll-gap-anim bottom-anim">
                  <ScrollTrigger trigger="#anim-block1 .bottom-anim" start="5px center" end="bottom center" scrub={0}>
                    <Timeline>
                      <Tween from={{ height: '0' }} to={{ height: '100%' }} ease="none"><span /></Tween>
                    </Timeline>
                  </ScrollTrigger>
                </div>
              </Container>
            </section>
            <section className="bday-content-block" id="anim-block2">
              <Container className="bday-container-sm">
                <ScrollTrigger
                  trigger="#anim-block2"
                  start="-100 center"
                  end="-100 center"
                  animation={true}
                  toggleActions="play none reverse none"
                >
                  <Tween
                    from={{ opacity: 0, y: 70 }}
                    to={{ opacity: 1, y: 0 }}
                    reverse={true}
                    stagger="0.3"
                    duration="0.8"
                  >
                    <div className="title-block">
                      <h3>{birthdayPage.section_content_2.title}</h3>
                    </div>
                  </Tween>
                </ScrollTrigger>
                <div className="scroll-line-anim scroll-gap-anim top-anim">
                  <ScrollTrigger trigger="#anim-block2 .top-anim" start="top center" end="bottom center" scrub={0}>
                    <Timeline>
                      <Tween from={{ height: '0' }} to={{ height: '100%' }} ease="none"><span /></Tween>
                    </Timeline>
                  </ScrollTrigger>
                </div>
                <div className="content-box">
                  <div className="scroll-box-anim">
                    <ScrollTrigger
                      trigger="#anim-block2 .scroll-box-anim"
                      start="5px center"
                      end="bottom center"
                      scrub={0}
                    >
                      <Timeline>
                        <Tween from={{ width: '0' }} to={{ width: '50%' }} ease="none"><span /></Tween>
                        <Tween from={{ height: '0' }} to={{ height: '100%' }} ease="none"><span /></Tween>
                        <Tween from={{ width: '0' }} to={{ width: '50%' }} ease="none"><span /></Tween>
                      </Timeline>
                    </ScrollTrigger>
                  </div>
                  <Row className="align-items-center">
                    <ScrollTrigger
                      trigger="#anim-block2 .content-box"
                      start="-100 center"
                      end="-100 center"
                      animation={true}
                      toggleActions="play none reverse none"
                    >
                      <Tween
                        from={{ opacity: 0, y: 70 }}
                        to={{ opacity: 1, y: 0 }}
                        reverse={true}
                        stagger="0.3"
                        duration="0.8"
                      >
                        <div className="col-md-6">
                          <div className="content-box__col">
                            <img data-src="/img/birthday/dental-checkup.png" alt="Zenyum" />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="content-box__col">
                            <h4>{birthdayPage.section_content_2.question}</h4>
                            <div dangerouslySetInnerHTML={{ __html: birthdayPage.section_content_2.answer }} />
                          </div>
                        </div>
                      </Tween>
                    </ScrollTrigger>
                  </Row>
                </div>
                <div className="scroll-line-anim scroll-gap-anim bottom-anim">
                  <ScrollTrigger trigger="#anim-block2 .bottom-anim" start="5px center" end="bottom center" scrub={0}>
                    <Timeline>
                      <Tween from={{ height: '0' }} to={{ height: '100%' }} ease="none"><span /></Tween>
                    </Timeline>
                  </ScrollTrigger>
                </div>
              </Container>
            </section>
            <section className="bday-content-block" id="anim-block3">
              <Container className="bday-container-sm">
                <ScrollTrigger
                  trigger="#anim-block3"
                  start="-100 center"
                  end="-100 center"
                  animation={true}
                  toggleActions="play none reverse none"
                >
                  <Tween
                    from={{ opacity: 0, y: 70 }}
                    to={{ opacity: 1, y: 0 }}
                    reverse={true}
                    stagger="0.3"
                    duration="0.8"
                  >
                    <div className="title-block">
                      <h3>{birthdayPage.section_content_3.title}</h3>
                    </div>
                  </Tween>
                </ScrollTrigger>
                <div className="scroll-line-anim scroll-gap-anim top-anim">
                  <ScrollTrigger trigger="#anim-block3 .top-anim" start="top center" end="bottom center" scrub={0}>
                    <Timeline>
                      <Tween from={{ height: '0' }} to={{ height: '100%' }} ease="none"><span /></Tween>
                    </Timeline>
                  </ScrollTrigger>
                </div>
                <div className="content-box">
                  <div className="scroll-box-anim">
                    <ScrollTrigger
                      trigger="#anim-block3 .scroll-box-anim"
                      start="5px center"
                      end="bottom center"
                      scrub={0}
                    >
                      <Timeline>
                        <Tween from={{ width: '0' }} to={{ width: '50%' }} ease="none"><span /></Tween>
                        <Tween from={{ height: '0' }} to={{ height: '100%' }} ease="none"><span /></Tween>
                        <Tween from={{ width: '0' }} to={{ width: '50%' }} ease="none"><span /></Tween>
                      </Timeline>
                    </ScrollTrigger>
                  </div>
                  <div className="tab-box">
                    <Nav tabs={true}>
                      <ScrollTrigger
                        trigger="#anim-block3 .content-box"
                        start="-100 center"
                        end="-100 center"
                        animation={true}
                        toggleActions="play none reverse none"
                      >
                        <Tween
                          from={{ opacity: 0, y: 70 }}
                          to={{ opacity: 1, y: 0 }}
                          reverse={true}
                          stagger="0.3"
                          duration="0.8"
                        >
                          <li className="nav-item">
                            <NavLink
                              className={({ active: kickstartTab === '1' })}
                              onClick={() => { setKickstartTab('1'); }}
                            >
                              <div className="tab-image">
                                <img data-src="/img/birthday/tab-treatment.png" alt="Zenyum" />
                              </div>
                              <p>{birthdayPage.section_content_3.tab_1.title}</p>
                            </NavLink>
                          </li>
                          <li className="nav-item">
                            <NavLink
                              className={({ active: kickstartTab === '2' })}
                              onClick={() => { setKickstartTab('2'); }}
                            >
                              <div className="tab-image">
                                <img data-src="/img/birthday/tab-price.png" alt="Zenyum" />
                              </div>
                              <p>{birthdayPage.section_content_3.tab_2.title}</p>
                            </NavLink>
                          </li>
                          <li className="nav-item">
                            <NavLink
                              className={({ active: kickstartTab === '3' })}
                              onClick={() => { setKickstartTab('3'); }}
                            >
                              <div className="tab-image">
                                <img data-src="/img/birthday/tab-app.png" alt="Zenyum" />
                              </div>
                              <p>{birthdayPage.section_content_3.tab_3.title}</p>
                            </NavLink>
                          </li>
                        </Tween>
                      </ScrollTrigger>
                    </Nav>
                    <ScrollTrigger
                      trigger="#anim-block3 .content-box"
                      start="-100 center"
                      end="-100 center"
                      animation={true}
                      toggleActions="play none reverse none"
                    >
                      <Tween
                        from={{ opacity: 0, y: 70 }}
                        to={{ opacity: 1, y: 0 }}
                        reverse={true}
                        delay="0.6"
                        stagger="0.3"
                        duration="0.8"
                      >
                        <div>
                          <TabContent activeTab={kickstartTab}>
                            <TabPane tabId="1">
                              <h4>{birthdayPage.section_content_3.tab_1.question}</h4>
                              <p>{birthdayPage.section_content_3.tab_1.answer}</p>
                            </TabPane>
                            <TabPane tabId="2">
                              <h4>{birthdayPage.section_content_3.tab_2.question}</h4>
                              <p>{birthdayPage.section_content_3.tab_2.answer}</p>
                            </TabPane>
                            <TabPane tabId="3">
                              <h4>{birthdayPage.section_content_3.tab_3.question}</h4>
                              <p>{birthdayPage.section_content_3.tab_3.answer}</p>
                            </TabPane>
                          </TabContent>
                        </div>
                      </Tween>
                    </ScrollTrigger>
                  </div>
                </div>
                <div className="scroll-line-anim scroll-gap-anim bottom-anim">
                  <ScrollTrigger trigger="#anim-block3 .bottom-anim" start="5px center" end="bottom center" scrub={0}>
                    <Timeline>
                      <Tween from={{ height: '0' }} to={{ height: '100%' }} ease="none"><span /></Tween>
                    </Timeline>
                  </ScrollTrigger>
                </div>
              </Container>
            </section>
            <section className="bday-content-block" id="anim-block4">
              <Container className="bday-container-sm">
                <ScrollTrigger
                  trigger="#anim-block4"
                  start="-100 center"
                  end="-100 center"
                  animation={true}
                  toggleActions="play none reverse none"
                >
                  <Tween
                    from={{ opacity: 0, y: 70 }}
                    to={{ opacity: 1, y: 0 }}
                    reverse={true}
                    stagger="0.3"
                    duration="0.8"
                  >
                    <div className="title-block">
                      <h3>{birthdayPage.section_content_4.title}</h3>
                    </div>
                  </Tween>
                </ScrollTrigger>
                <div className="scroll-line-anim scroll-gap-anim top-anim">
                  <ScrollTrigger trigger="#anim-block4 .top-anim" start="top center" end="bottom center" scrub={0}>
                    <Timeline>
                      <Tween from={{ height: '0' }} to={{ height: '100%' }} ease="none"><span /></Tween>
                    </Timeline>
                  </ScrollTrigger>
                </div>
                <div className="content-box">
                  <div className="scroll-box-anim">
                    <ScrollTrigger
                      trigger="#anim-block4 .scroll-box-anim"
                      start="5px center"
                      end="bottom center"
                      scrub={0}
                    >
                      <Timeline>
                        <Tween from={{ width: '0' }} to={{ width: '50%' }} ease="none"><span /></Tween>
                        <Tween from={{ height: '0' }} to={{ height: '100%' }} ease="none"><span /></Tween>
                        <Tween from={{ width: '0' }} to={{ width: '50%' }} ease="none"><span /></Tween>
                      </Timeline>
                    </ScrollTrigger>
                  </div>
                  <Row className="align-items-center">
                    <ScrollTrigger
                      trigger="#anim-block4 .content-box"
                      start="-100 center"
                      end="-100 center"
                      animation={true}
                      toggleActions="play none reverse none"
                    >
                      <Tween
                        from={{ opacity: 0, y: 70 }}
                        to={{ opacity: 1, y: 0 }}
                        reverse={true}
                        stagger="0.3"
                        duration="0.8"
                      >
                        <div className="col-md-6">
                          <div className="content-box__col">
                            <img data-src="/img/birthday/second-dental-checkup.png" alt="Zenyum" />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="content-box__col">
                            <h4>{birthdayPage.section_content_4.question}</h4>
                            <div dangerouslySetInnerHTML={{ __html: birthdayPage.section_content_4.answer }} />
                          </div>
                        </div>
                      </Tween>
                    </ScrollTrigger>
                  </Row>
                </div>
                <div className="scroll-line-anim scroll-gap-anim bottom-anim">
                  <ScrollTrigger trigger="#anim-block4 .bottom-anim" start="5px center" end="bottom center" scrub={0}>
                    <Timeline>
                      <Tween from={{ height: '0' }} to={{ height: '100%' }} ease="none"><span /></Tween>
                    </Timeline>
                  </ScrollTrigger>
                </div>
              </Container>
            </section>
            <section className="bday-content-block" id="anim-block5">
              <Container className="bday-container-sm">
                <ScrollTrigger
                  trigger="#anim-block5"
                  start="-100 center"
                  end="-100 center"
                  animation={true}
                  toggleActions="play none reverse none"
                >
                  <Tween
                    from={{ opacity: 0, y: 70 }}
                    to={{ opacity: 1, y: 0 }}
                    reverse={true}
                    stagger="0.3"
                    duration="0.8"
                  >
                    <div className="title-block">
                      <h3>{birthdayPage.section_content_5.title}</h3>
                    </div>
                  </Tween>
                </ScrollTrigger>
                <div className="scroll-line-anim scroll-gap-anim top-anim">
                  <ScrollTrigger trigger="#anim-block5 .top-anim" start="top center" end="bottom center" scrub={0}>
                    <Timeline>
                      <Tween from={{ height: '0' }} to={{ height: '100%' }} ease="none"><span /></Tween>
                    </Timeline>
                  </ScrollTrigger>
                </div>
                <div className="content-box">
                  <div className="scroll-box-anim">
                    <ScrollTrigger
                      trigger="#anim-block5 .scroll-box-anim"
                      start="5px center"
                      end="bottom center"
                      scrub={0}
                    >
                      <Timeline>
                        <Tween from={{ width: '0' }} to={{ width: '50%' }} ease="none"><span /></Tween>
                        <Tween from={{ height: '0' }} to={{ height: '100%' }} ease="none"><span /></Tween>
                        <Tween from={{ width: '0' }} to={{ width: '50%' }} ease="none"><span /></Tween>
                      </Timeline>
                    </ScrollTrigger>
                  </div>
                  <Row>
                    <ScrollTrigger
                      trigger="#anim-block5 .content-box"
                      start="-100 center"
                      end="-100 center"
                      animation={true}
                      toggleActions="play none reverse none"
                    >
                      <Tween
                        from={{ opacity: 0, y: 70 }}
                        to={{ opacity: 1, y: 0 }}
                        reverse={true}
                        stagger="0.3"
                        duration="0.8"
                      >
                        <div className="col-md-6">
                          <div className="content-box__col">
                            <img data-src="/img/birthday/home-treatment.png" alt="Zenyum" />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="content-box__col">
                            <p className="block-label">{birthdayPage.select_text}</p>
                            <div className="accordion-box">
                              {birthdayPage.section_content_5.data
                              && birthdayPage.section_content_5.data.length > 0
                              && birthdayPage.section_content_5.data.map((row, index) => (
                                row.question && (
                                <Collapse
                                  id={index}
                                  isToggled={toggleTreatment === index}
                                  setTogglequestion={setToggleTreatment}
                                  title={row.question}
                                  content={row.answer}
                                />
                                )
                              ))}
                            </div>
                          </div>
                        </div>
                      </Tween>
                    </ScrollTrigger>
                  </Row>
                </div>
                <div className="scroll-line-anim scroll-gap-anim bottom-anim">
                  <ScrollTrigger trigger="#anim-block5 .bottom-anim" start="5px center" end="bottom center" scrub={0}>
                    <Timeline>
                      <Tween from={{ height: '0' }} to={{ height: '100%' }} ease="none"><span /></Tween>
                    </Timeline>
                  </ScrollTrigger>
                </div>
              </Container>
            </section>
            <section className="bday-content-block" id="anim-block6">
              <Container className="bday-container-sm">
                <ScrollTrigger
                  trigger="#anim-block6"
                  start="-100 center"
                  end="-100 center"
                  animation={true}
                  toggleActions="play none reverse none"
                >
                  <Tween
                    from={{ opacity: 0, y: 70 }}
                    to={{ opacity: 1, y: 0 }}
                    reverse={true}
                    stagger="0.3"
                    duration="0.8"
                  >
                    <div className="title-block">
                      <h3>{birthdayPage.section_content_6.title}</h3>
                    </div>
                  </Tween>
                </ScrollTrigger>
                <div className="scroll-line-anim scroll-gap-anim top-anim">
                  <ScrollTrigger trigger="#anim-block6 .top-anim" start="top center" end="bottom center" scrub={0}>
                    <Timeline>
                      <Tween from={{ height: '0' }} to={{ height: '100%' }} ease="none"><span /></Tween>
                    </Timeline>
                  </ScrollTrigger>
                </div>
                <div className="content-box">
                  <div className="scroll-box-anim">
                    <ScrollTrigger
                      trigger="#anim-block6 .scroll-box-anim"
                      start="5px center"
                      end="bottom center"
                      scrub={0}
                    >
                      <Timeline>
                        <Tween from={{ width: '0' }} to={{ width: '50%' }} ease="none"><span /></Tween>
                        <Tween from={{ height: '0' }} to={{ height: '100%' }} ease="none"><span /></Tween>
                        <Tween from={{ width: '0' }} to={{ width: '50%' }} ease="none"><span /></Tween>
                      </Timeline>
                    </ScrollTrigger>
                  </div>
                  <div className="tab-box">
                    <Nav tabs={true}>
                      <ScrollTrigger
                        trigger="#anim-block6 .content-box"
                        start="-100 center"
                        end="-100 center"
                        animation={true}
                        toggleActions="play none reverse none"
                      >
                        <Tween
                          from={{ opacity: 0, y: 70 }}
                          to={{ opacity: 1, y: 0 }}
                          reverse={true}
                          stagger="0.3"
                          duration="0.8"
                        >
                          <li className="nav-item">
                            <NavLink
                              className={({ active: smileTab === '1' })}
                              onClick={() => { setSmileTab('1'); }}
                            >
                              <div className="tab-image">
                                <img data-src="/img/birthday/avatar-venise.png" alt="Zenyum" />
                              </div>
                            </NavLink>
                          </li>
                          <li className="nav-item">
                            <NavLink
                              className={({ active: smileTab === '2' })}
                              onClick={() => { setSmileTab('2'); }}
                            >
                              <div className="tab-image">
                                <img data-src="/img/birthday/avatar-shabrina.png" alt="Zenyum" />
                              </div>
                            </NavLink>
                          </li>
                          <li className="nav-item">
                            <NavLink
                              className={({ active: smileTab === '3' })}
                              onClick={() => { setSmileTab('3'); }}
                            >
                              <div className="tab-image">
                                <img data-src="/img/birthday/avatar-david.png" alt="Zenyum" />
                              </div>
                            </NavLink>
                          </li>
                        </Tween>
                      </ScrollTrigger>
                    </Nav>
                    <ScrollTrigger
                      trigger="#anim-block6 .content-box"
                      start="-100 center"
                      end="-100 center"
                      animation={true}
                      toggleActions="play none reverse none"
                    >
                      <Tween
                        from={{ opacity: 0, y: 70 }}
                        to={{ opacity: 1, y: 0 }}
                        reverse={true}
                        delay="0.6"
                        stagger="0.3"
                        duration="0.8"
                      >
                        <div>
                          <TabContent activeTab={smileTab}>
                            <TabPane tabId="1">
                              <p>{birthdayPage.section_content_6.tab_1.content}</p>
                              <p><strong>{birthdayPage.section_content_6.tab_1.author}</strong></p>
                            </TabPane>
                            <TabPane tabId="2">
                              <p>{birthdayPage.section_content_6.tab_2.content}</p>
                              <p><strong>{birthdayPage.section_content_6.tab_2.author}</strong></p>
                            </TabPane>
                            <TabPane tabId="3">
                              <p>{birthdayPage.section_content_6.tab_3.content}</p>
                              <p><strong>{birthdayPage.section_content_6.tab_3.author}</strong></p>
                            </TabPane>
                          </TabContent>
                        </div>
                      </Tween>
                    </ScrollTrigger>
                  </div>
                </div>
                <div className="scroll-line-anim scroll-gap-anim bottom-anim">
                  <ScrollTrigger trigger="#anim-block6 .bottom-anim" start="5px center" end="bottom center" scrub={0}>
                    <Timeline>
                      <Tween from={{ height: '0' }} to={{ height: '100%' }} ease="none"><span /></Tween>
                    </Timeline>
                  </ScrollTrigger>
                </div>
              </Container>
            </section>
            <section className="bday-content-block" id="anim-block7">
              <Container className="bday-container-sm">
                <ScrollTrigger
                  trigger="#anim-block7"
                  start="-100 center"
                  end="-100 center"
                  animation={true}
                  toggleActions="play none reverse none"
                >
                  <Tween
                    from={{ opacity: 0, y: 70 }}
                    to={{ opacity: 1, y: 0 }}
                    reverse={true}
                    stagger="0.3"
                    duration="0.8"
                  >
                    <div className="title-block">
                      <h3 dangerouslySetInnerHTML={{ __html: birthdayPage.section_content_7.title }} />
                    </div>
                  </Tween>
                </ScrollTrigger>
                <div className="scroll-line-anim scroll-gap-anim top-anim">
                  <ScrollTrigger trigger="#anim-block7 .top-anim" start="top center" end="bottom center" scrub={0}>
                    <Timeline>
                      <Tween from={{ height: '0' }} to={{ height: '100%' }} ease="none"><span /></Tween>
                    </Timeline>
                  </ScrollTrigger>
                </div>
                <div className="content-box">
                  <div className="scroll-box-anim">
                    <ScrollTrigger
                      trigger="#anim-block7 .scroll-box-anim"
                      start="5px center"
                      end="bottom center"
                      scrub={0}
                    >
                      <Timeline>
                        <Tween from={{ width: '0' }} to={{ width: '50%' }} ease="none"><span /></Tween>
                        <Tween from={{ height: '0' }} to={{ height: '100%' }} ease="none"><span /></Tween>
                        <Tween from={{ width: '0' }} to={{ width: '50%' }} ease="none"><span /></Tween>
                      </Timeline>
                    </ScrollTrigger>
                  </div>
                  <Row className="align-items-center">
                    <ScrollTrigger
                      trigger="#anim-block7 .content-box"
                      start="-100 center"
                      end="-100 center"
                      animation={true}
                      toggleActions="play none reverse none"
                    >
                      <Tween
                        from={{ opacity: 0, y: 70 }}
                        to={{ opacity: 1, y: 0 }}
                        reverse={true}
                        stagger="0.3"
                        duration="0.8"
                      >
                        <div className="col-md-6">
                          <div className="content-box__col">
                            <img data-src="/img/birthday/what-next.png" alt="Zenyum" />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="content-box__col mr-0">
                            <h4><span>{birthdayPage.section_content_7.question}</span></h4>
                            <div dangerouslySetInnerHTML={{ __html: birthdayPage.section_content_7.answer }} />
                          </div>
                        </div>
                      </Tween>
                    </ScrollTrigger>
                  </Row>
                </div>
                <div className="scroll-line-anim scroll-gap-anim bottom-anim">
                  <ScrollTrigger trigger="#anim-block7 .bottom-anim" start="5px center" end="bottom center" scrub={0}>
                    <Timeline>
                      <Tween from={{ height: '0' }} to={{ height: '100%' }} ease="none"><span /></Tween>
                    </Timeline>
                  </ScrollTrigger>
                </div>
              </Container>
            </section>
          </div>
                &nbsp;
        </div>
        {productLinks && (
          <section className="birthday-video">
            <Container className="bday-container">
              <h2>{birthdayPage.video_title}</h2>
              <div className="video-frame-wrap">
                <iframe
                  className="video-frame"
                  title="Zenyum Birthday"
                  src={`${productLinks}?controls=0`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen={true}
                />
              </div>
            </Container>
          </section>
        )}
        {country !== 'th' && country !== 'tw' && (
          <section className="see-the-difference birthday-difference">
            <Container className="bday-container">
              <div className="wrapper">
                <Row>
                  <Col lg="5">
                    <h2>{birthdayPage.birthday_smile.title}</h2>
                    <p>{birthdayPage.birthday_smile.description}</p>
                  </Col>
                  <Col lg="7">
                    <div className="carouselWrapper">
                      <Slider {...HowZenyumWorksComparisonSlider} ref={comparisonSlider}>
                        {beforeAfterImages && beforeAfterImages.length > 0 && beforeAfterImages.map((row) => (
                          <div className="slide-wrapper">
                            <div className="beforeafter-slide-wrapper">
                              <div
                                className="before-background"
                                style={{ backgroundImage: `url(/img/birthday/before-after/${row.before})` }}
                              />
                              <div
                                className="after-background"
                                style={{ backgroundImage: `url(/img/birthday/before-after/${row.after})` }}
                              />
                            </div>
                          </div>
                        ))}
                      </Slider>
                    </div>
                  </Col>
                </Row>
              </div>
            </Container>
          </section>
        )}
        <section className="birthday-footerbox">
          <Container className="bday-container">
            <Row>
              <Col lg="6">
                <h2>{birthdayPage.birthday_footer_catch.title}</h2>
                <div dangerouslySetInnerHTML={{ __html: birthdayPage.birthday_footer_catch.content }} />
              </Col>
              <Col lg="6">
                {/* <img src={gumletLoader({ src: `/img/birthday/looking-catch.png`, width: 600 })} alt="Zenyum" /> */}
                <img data-src="/img/birthday/looking-catch.png" alt="Zenyum" />
              </Col>
            </Row>
          </Container>
        </section>
      </main>
      <footer className="birthday-footer">
        <div className="footer-content">
          <img src="/img/birthday/footer-ending.png" alt="Zenyum" />
          <h2 dangerouslySetInnerHTML={{ __html: birthdayPage.birthday_footer.title }} />
          <p>{birthdayPage.birthday_footer.description}</p>
          <p className="small">{birthdayPage.birthday_footer.label}</p>
        </div>
      </footer>
    </div>
  );
};

export async function getStaticProps({ params }) {
  const lngDict = {};
  const pageId = PAGEID.HOW_ZENYUM_WORKS;
  const keys = getTranslationKeys(pageId);
  const { default: lngDictAll = {} } = await import(
    `../../locales/${contentLanguageMap[params.lng]}.json`
  );
  keys.forEach((data) => {
    lngDict[data] = lngDictAll[data];
  });
  return {
    props: { lng: params.lng, lngDict, pageId }
  };
}
export async function getStaticPaths() {
  return {
    paths: languages.map((l) => ({ params: { lng: l } })),
    fallback: false
  };
}

export default Birthday;
